import Box from "@mui/material/Box";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
// components
import AppModal from "../../../../components/ui/AppModal";

export interface InsuranceDetailsItem {
  label: string;
  description: string;
  included: boolean;
}

interface EuropcarInsuranceDetailsModalProps {
  open: boolean;
  onClose: () => void;
  ldwExcessDisplay: string;
}

/**
 * Modal showing detailed protection plan info (Basic plan).
 * Uses AppModal for consistency; content is mock data for now.
 */
export default function EuropcarInsuranceDetailsModal({
  open,
  onClose,
  ldwExcessDisplay
}: EuropcarInsuranceDetailsModalProps) {
  const { t } = useTranslation();
  const planName = t("transport.europcarInsurance.planName");
  const collisionDamageDescription =
    ldwExcessDisplay === "-"
      ? t("transport.europcarInsurance.modal.collisionDamageNoExcess")
      : t("transport.europcarInsurance.modal.collisionDamageWithExcess", { excess: ldwExcessDisplay });

  const theftDescription =
    ldwExcessDisplay === "-"
      ? t("transport.europcarInsurance.modal.theftNoExcess")
      : t("transport.europcarInsurance.modal.theftWithExcess", { excess: ldwExcessDisplay });

  const plan = {
    planName,
    excess: ldwExcessDisplay,
    leftColumn: [
      {
        label: t("transport.europcarInsurance.modal.labels.collisionDamageProtection"),
        description: collisionDamageDescription,
        included: true
      },
      {
        label: t("transport.europcarInsurance.modal.labels.windscreenGlassLightsTires"),
        description: t("transport.europcarInsurance.modal.windscreenGlassLightsTiresDescription"),
        included: false
      },
      {
        label: t("transport.europcarInsurance.modal.labels.personalBelongingsProtection"),
        description: t("transport.europcarInsurance.modal.personalBelongingsDescription"),
        included: false
      }
    ] as InsuranceDetailsItem[],
    rightColumn: [
      {
        label: t("transport.europcarInsurance.modal.labels.theftProtection"),
        description: theftDescription,
        included: true
      },
      {
        label: t("transport.europcarInsurance.modal.labels.personalAccidentProtection"),
        description: t("transport.europcarInsurance.modal.personalAccidentDescription"),
        included: false
      }
    ] as InsuranceDetailsItem[]
  };

  const renderColumn = (items: InsuranceDetailsItem[]) =>
    items.map((item, index) => (
      <Box key={index} sx={{ mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          {item.included ? (
            <CheckIcon sx={{ fontSize: 20, color: "success.main", mt: 0.25, flexShrink: 0 }} />
          ) : (
            <CloseIcon sx={{ fontSize: 20, color: "text.disabled", mt: 0.25, flexShrink: 0 }} />
          )}
          <Box>
            <Box sx={{ fontFamily: "Neutraface2Demi", fontSize: 14, mb: 0.5 }}>{item.label}</Box>
            <Box sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.5 }}>{item.description}</Box>
          </Box>
        </Box>
      </Box>
    ));

  return (
    <AppModal
      isOpen={open}
      handleCloseModal={onClose}
      handleOpenModal={() => {}}
      title={`${planName} ★`}
      modalSize="md"
      showCloseIcon
    >
      <>
        <Box sx={{ fontFamily: "Neutraface2Demi", fontSize: 14, color: "text.secondary", textAlign: "center", mb: 3 }}>
          {t("transport.europcarInsurance.excessLabel")}: {plan.excess}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: { xs: 2, sm: 4 }
          }}
        >
          <Box>{renderColumn(plan.leftColumn)}</Box>
          <Box>{renderColumn(plan.rightColumn)}</Box>
        </Box>
      </>
    </AppModal>
  );
}
